import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { AlertCircle, CreditCard, Info, Loader2, Wallet } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  PAYABLE_INVOICE_BUCKET,
  type PayableInvoice,
  type PayableInvoicesResponse,
  type TakePaymentMethod,
  type TakePaymentParams,
  useGetPayableInvoices,
  useTakePaymentMutation,
  useTakePaymentPreview,
} from '~entities/payment/api/take-payment';

import { useGetCurrency } from '~shared/api/currency-api';
import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import { Separator } from '~shared/shadcn/ui/separator';
import { Textarea } from '~shared/shadcn/ui/textarea';

interface TakePaymentDialogProps {
  parentId: string;
  /** Scopes the charge list to one student; omit to show the whole family. */
  studentId?: string;
}

const SECTIONS = [
  {
    key: 'pastDue' as const,
    bucket: PAYABLE_INVOICE_BUCKET.PAST_DUE,
    title: 'Past due',
    accent: 'text-red-600',
  },
  {
    key: 'dueNow' as const,
    bucket: PAYABLE_INVOICE_BUCKET.DUE_NOW,
    title: 'Due now',
    accent: 'text-amber-600',
  },
  {
    key: 'upcoming' as const,
    bucket: PAYABLE_INVOICE_BUCKET.UPCOMING,
    title: 'Upcoming',
    accent: 'text-muted-foreground',
  },
];

const todayForDateInput = () => format(new Date(), 'yyyy-MM-dd');
const CURRENCY_EPSILON = 0.001;

/**
 * Due dates are date-only values the API sends at UTC midnight, so they are read
 * back in UTC. Formatting them in the browser zone shows the previous calendar
 * day west of UTC and disagrees with the exports and transaction tables.
 */
const formatDueDate = (value: string | null) => {
  if (!value) return 'No due date';
  try {
    return formatInTimeZone(parseISO(value), 'UTC', 'MMM d, yyyy');
  } catch {
    return 'No due date';
  }
};

function defaultSelection(data: PayableInvoicesResponse | undefined) {
  if (!data) return new Set<string>();
  return new Set(
    [...data.groups.pastDue, ...data.groups.dueNow]
      .filter((invoice) => invoice.isPayable)
      .map((invoice) => invoice.invoiceId),
  );
}

/** Lets a refetch keep the existing Set when nothing actually changed. */
function haveSameMembers(a: Set<string>, b: Set<string>) {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

export const TakePaymentDialog = ({
  parentId,
  studentId,
}: TakePaymentDialogProps) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<TakePaymentMethod>('card');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [draftAmounts, setDraftAmounts] = useState<Record<string, string>>({});
  const [paymentDate, setPaymentDate] = useState(todayForDateInput());
  const [checkNumber, setCheckNumber] = useState('');
  const [note, setNote] = useState('');
  const [offlineAmountDraft, setOfflineAmountDraft] = useState<
    string | undefined
  >();
  const hasSeededSelection = useRef(false);

  const { data: currencyDetails } = useGetCurrency();
  const symbol = currencyDetails?.symbol ?? '$';

  const {
    data,
    isLoading,
    isFetching: isRefreshingCharges,
  } = useGetPayableInvoices(
    { parentId, studentId },
    { enabled: open && Boolean(parentId) },
  );
  const takePaymentMutation = useTakePaymentMutation();

  const allowSelective = data?.settings.allowSelectiveInvoicePayment ?? true;
  const allowPartial = data?.settings.allowPartialInvoicePayment ?? false;

  const invoicesById = useMemo(() => {
    const map = new Map<string, PayableInvoice>();
    for (const section of SECTIONS) {
      for (const invoice of data?.groups[section.key] ?? []) {
        map.set(invoice.invoiceId, invoice);
      }
    }
    return map;
  }, [data]);

  useEffect(() => {
    if (!open) {
      hasSeededSelection.current = false;
      return;
    }
    if (!data) return;

    // Seeded once per opening: a background refetch must not wipe the charges
    // and amounts the user has already entered.
    if (!hasSeededSelection.current) {
      hasSeededSelection.current = true;
      setSelectedIds(defaultSelection(data));
      setDraftAmounts({});
      return;
    }

    // With selective payment off the checkboxes are disabled, so the selection is
    // policy-derived rather than user-owned. It has to track the data: a charge
    // that becomes payable after the dialog opened (tuition posted, or an online
    // attempt failed and released it) could never be ticked by hand, and the
    // payment would quietly under-cover what is due.
    if (!allowSelective) {
      setSelectedIds((current) => {
        const next = defaultSelection(data);
        return haveSameMembers(current, next) ? current : next;
      });
      return;
    }

    // A refetch can retire a charge (paid elsewhere, or a payment started on it),
    // so drop it from the selection to keep the summary and request honest.
    // Newly payable charges are left alone here — the user is curating this list
    // and can tick them themselves.
    const stillPayable = new Set(
      [
        ...data.groups.pastDue,
        ...data.groups.dueNow,
        ...data.groups.upcoming,
      ]
        .filter((invoice) => invoice.isPayable)
        .map((invoice) => invoice.invoiceId),
    );
    setSelectedIds((current) => {
      const next = new Set(
        [...current].filter((invoiceId) => stillPayable.has(invoiceId)),
      );
      return next.size === current.size ? current : next;
    });
  }, [open, data, allowSelective]);

  const allocations = useMemo(() => {
    const rows: Array<{ invoiceId: string; amount: number }> = [];
    for (const invoiceId of selectedIds) {
      const invoice = invoicesById.get(invoiceId);
      if (!invoice) continue;

      // Only an untouched field means "charge the whole balance". A field the
      // user edited and then cleared must fail validation instead of silently
      // charging the full amount behind a blank input.
      const draft = allowPartial ? draftAmounts[invoiceId] : undefined;
      rows.push({
        invoiceId,
        amount:
          draft === undefined ? invoice.remainingBalance : Number(draft.trim()),
      });
    }
    return rows;
  }, [selectedIds, invoicesById, draftAmounts, allowPartial]);

  const localTotal = useMemo(() => {
    // A draft being edited can parse to NaN or Infinity (an exponent like 1e999
    // is a valid number input). Skipping those keeps the summary readable while
    // validation separately blocks the payment.
    const sum = allocations.reduce(
      (total, row) => (Number.isFinite(row.amount) ? total + row.amount : total),
      0,
    );
    return Math.round(sum * 100) / 100;
  }, [allocations]);

  const amountError = useMemo(() => {
    for (const row of allocations) {
      const invoice = invoicesById.get(row.invoiceId);
      if (!invoice) continue;
      if (!Number.isFinite(row.amount) || row.amount <= 0) {
        return 'Enter an amount greater than 0 for each selected charge.';
      }
      if (row.amount > invoice.remainingBalance + CURRENCY_EPSILON) {
        return 'An amount exceeds the charge’s remaining balance.';
      }
    }
    return null;
  }, [allocations, invoicesById]);

  const offlineTotalAmount =
    offlineAmountDraft === undefined
      ? localTotal
      : Number(offlineAmountDraft.trim());

  const offlineAmountError = useMemo(() => {
    if (method === 'card') return null;
    if (!Number.isFinite(offlineTotalAmount) || offlineTotalAmount <= 0) {
      return 'Enter an amount received greater than 0.';
    }
    if (offlineTotalAmount < localTotal - CURRENCY_EPSILON) {
      return 'Amount received cannot be less than the amount applied to charges.';
    }
    const cents = offlineTotalAmount * 100;
    if (Math.abs(cents - Math.round(cents)) > CURRENCY_EPSILON) {
      return 'Amount received can have at most 2 decimal places.';
    }
    if (offlineTotalAmount > 1_000_000) {
      return 'Amount received cannot exceed 1,000,000.';
    }
    return null;
  }, [localTotal, method, offlineTotalAmount]);

  const paymentAmountError = amountError ?? offlineAmountError;
  const overpaymentAmount =
    method !== 'card' && offlineAmountError === null
      ? Math.round(Math.max(0, offlineTotalAmount - localTotal) * 100) / 100
      : 0;

  const requestBase: TakePaymentParams = useMemo(
    () => ({
      parentId,
      studentId,
      method,
      invoices: allocations,
      ...(method === 'card' ? {} : { totalAmount: offlineTotalAmount }),
    }),
    [parentId, studentId, method, allocations, offlineTotalAmount],
  );

  const canPreview =
    open &&
    method === 'card' &&
    allocations.length > 0 &&
    amountError === null;

  const { data: preview, isFetching: isPreviewFetching, error: previewError } =
    useTakePaymentPreview(requestBase, { enabled: canPreview });

  const previewErrorText = useMemo(() => {
    if (!previewError) return null;
    const message = (
      previewError as { response?: { data?: { message?: string | Array<string> } } }
    ).response?.data?.message;
    return Array.isArray(message) ? message[0] : (message ?? null);
  }, [previewError]);

  // React Query keeps the last successful data when a refetch fails, so `preview`
  // still holds the previous selection's numbers while the error alert is shown.
  // Treating that as unavailable stops staff charging a total the backend has not
  // confirmed for what is currently selected.
  const confirmedPreview = previewError ? undefined : preview;

  const resetForm = () => {
    setMethod('card');
    setSelectedIds(new Set());
    setDraftAmounts({});
    setPaymentDate(todayForDateInput());
    setCheckNumber('');
    setNote('');
    setOfflineAmountDraft(undefined);
  };

  const handleSubmit = async () => {
    // The button is disabled for these cases, but re-checking keeps a stale
    // preview from being charged if a submit is triggered another way.
    if (submitDisabled) {
      return;
    }

    try {
      const response = await takePaymentMutation.mutateAsync({
        ...requestBase,
        ...(method === 'card'
          ? {}
          : {
              paymentDate,
              checkNumber: method === 'check' ? checkNumber.trim() : undefined,
              note: note.trim() || undefined,
            }),
      });

      toast.success(response.message);
      setOpen(false);
      resetForm();
    } catch (error) {
      const message = (
        error as { response?: { data?: { message?: string | Array<string> } } }
      ).response?.data?.message;
      toast.error(
        (Array.isArray(message) ? message[0] : message) ??
          'Failed to take payment',
      );
    }
  };

  const submitDisabled =
    allocations.length === 0 ||
    paymentAmountError !== null ||
    takePaymentMutation.isPending ||
    (method === 'card' && (isPreviewFetching || !confirmedPreview)) ||
    (method === 'check' && !checkNumber.trim()) ||
    (method !== 'card' && !paymentDate);

  const total =
    method === 'card'
      ? (confirmedPreview?.chargeTotal ?? localTotal)
      : Number.isFinite(offlineTotalAmount)
        ? offlineTotalAmount
        : 0;
  const nothingPayable = !isLoading && data != null && invoicesById.size === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          resetForm();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant='secondary'
          size='sm'
          className='shrink-0'
          title='Take a Payment'
          aria-label='Take a Payment'
        >
          <Wallet className='size-4' />
          <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
            Take a Payment
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className='max-h-[88vh] overflow-auto sm:max-w-[720px]'>
        <DialogHeader>
          <DialogTitle>Take a Payment</DialogTitle>
          <DialogDescription>
            Charge the card on file or log a cash/check payment, and apply it
            straight to the charges you pick.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-5 py-2'>
          <div className='grid gap-2'>
            <Label>Payment method</Label>
            <RadioGroup
              value={method}
              className='flex flex-wrap gap-5'
              onValueChange={(value) => {
                setMethod(value as TakePaymentMethod);
              }}
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='card' id='take-payment-card' />
                <Label
                  htmlFor='take-payment-card'
                  className='flex cursor-pointer items-center gap-1.5'
                >
                  <CreditCard className='size-4' />
                  Card on file
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='cash' id='take-payment-cash' />
                <Label htmlFor='take-payment-cash' className='cursor-pointer'>
                  Cash
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='check' id='take-payment-check' />
                <Label htmlFor='take-payment-check' className='cursor-pointer'>
                  Check
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          <div className='grid gap-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <Label>Charges</Label>
                {/* A background refresh keeps the list on screen, so it is
                    reported here instead of replacing the charges. */}
                {!isLoading && isRefreshingCharges && (
                  <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                    <Loader2 className='size-3 animate-spin' />
                    Refreshing
                  </span>
                )}
              </div>
              {data && (
                <span className='text-xs text-muted-foreground'>
                  Amount due today: {symbol}
                  {data.totals.amountDue.toFixed(2)}
                </span>
              )}
            </div>

            {!allowSelective && (
              <Alert className='border-blue-200 bg-blue-50 text-blue-800'>
                <Info className='size-4' />
                <AlertDescription className='text-xs'>
                  This studio applies payments oldest-due-first, so the amount
                  is spread across charges in due-date order.
                </AlertDescription>
              </Alert>
            )}

            {isLoading && (
              <div className='flex items-center justify-center gap-2 rounded-md border p-6 text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Loading charges
              </div>
            )}

            {nothingPayable && (
              <div className='rounded-md border p-6 text-center text-sm text-muted-foreground'>
                This family has no charges to collect right now.
              </div>
            )}

            {!isLoading && !nothingPayable && (
              <div className='space-y-4 rounded-md border p-3'>
                {SECTIONS.map((section) => {
                  const rows = data?.groups[section.key] ?? [];
                  if (rows.length === 0) return null;

                  return (
                    <div key={section.key} className='space-y-2'>
                      <div className='flex items-center justify-between'>
                        <h4
                          className={`text-sm font-semibold ${section.accent}`}
                        >
                          {section.title}
                        </h4>
                        <span className='text-sm font-medium tabular-nums'>
                          {symbol}
                          {(data?.totals[section.key] ?? 0).toFixed(2)}
                        </span>
                      </div>
                      {rows.map((invoice) => (
                        <ChargeRow
                          key={invoice.invoiceId}
                          invoice={invoice}
                          symbol={symbol}
                          checked={selectedIds.has(invoice.invoiceId)}
                          disabled={!invoice.isPayable || !allowSelective}
                          allowPartial={allowPartial}
                          draftAmount={draftAmounts[invoice.invoiceId]}
                          onToggle={(checked) => {
                            setSelectedIds((current) => {
                              const next = new Set(current);
                              if (checked) {
                                next.add(invoice.invoiceId);
                              } else {
                                next.delete(invoice.invoiceId);
                              }
                              return next;
                            });
                          }}
                          onAmountChange={(value) => {
                            setDraftAmounts((current) => ({
                              ...current,
                              [invoice.invoiceId]: value,
                            }));
                          }}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {method !== 'card' && (
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='grid gap-2'>
                <Label htmlFor='take-payment-amount-received'>
                  Amount received
                </Label>
                <div className='relative'>
                  <span className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground'>
                    {symbol}
                  </span>
                  <Input
                    id='take-payment-amount-received'
                    type='number'
                    inputMode='decimal'
                    min={0.01}
                    max={1_000_000}
                    step={0.01}
                    value={offlineAmountDraft ?? localTotal.toFixed(2)}
                    onChange={(event) => {
                      setOfflineAmountDraft(event.target.value);
                    }}
                    className='pl-7 tabular-nums'
                  />
                </div>
              </div>
              <div className='grid gap-2'>
                <Label htmlFor='take-payment-date'>Payment date</Label>
                <Input
                  id='take-payment-date'
                  type='date'
                  value={paymentDate}
                  onChange={(event) => {
                    setPaymentDate(event.target.value);
                  }}
                />
              </div>
              {method === 'check' && (
                <div className='grid gap-2'>
                  <Label htmlFor='take-payment-check-number'>
                    Check number
                  </Label>
                  <Input
                    id='take-payment-check-number'
                    placeholder='Enter check number'
                    value={checkNumber}
                    onChange={(event) => {
                      setCheckNumber(event.target.value);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {method !== 'card' && (
            <div className='grid gap-2'>
              <Label htmlFor='take-payment-note'>Notes / reference</Label>
              <Textarea
                id='take-payment-note'
                placeholder='Optional memo or reference'
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
            </div>
          )}

          <Separator />

          <div className='space-y-1.5 text-sm'>
            <div className='flex justify-between text-muted-foreground'>
              <span>
                {allocations.length}{' '}
                {allocations.length === 1 ? 'charge' : 'charges'} selected
              </span>
              <span className='tabular-nums'>
                {symbol}
                {(method === 'card'
                  ? (confirmedPreview?.allocatedTotal ?? localTotal)
                  : localTotal
                ).toFixed(2)}
              </span>
            </div>
            {method === 'card' &&
              (confirmedPreview?.transactionFeeAmount ?? 0) > 0 && (
                <div className='flex justify-between text-muted-foreground'>
                  <span>Transaction fee</span>
                  <span className='tabular-nums'>
                    {symbol}
                    {(confirmedPreview?.transactionFeeAmount ?? 0).toFixed(2)}
                  </span>
                </div>
              )}
            {overpaymentAmount > 0 && (
              <div className='flex justify-between text-amber-700'>
                <span>Parent wallet credit</span>
                <span className='tabular-nums'>
                  {symbol}
                  {overpaymentAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div className='flex justify-between font-semibold'>
              <span>Total</span>
              <span className='flex items-center gap-1.5 tabular-nums'>
                {method === 'card' && isPreviewFetching && (
                  <Loader2 className='size-3.5 animate-spin text-muted-foreground' />
                )}
                {symbol}
                {total.toFixed(2)}
              </span>
            </div>
          </div>

          {paymentAmountError && (
            <Alert className='border-red-200 bg-red-50 text-red-700'>
              <AlertCircle className='size-4' />
              <AlertDescription className='text-xs'>
                {paymentAmountError}
              </AlertDescription>
            </Alert>
          )}
          {!paymentAmountError && previewErrorText && (
            <Alert className='border-red-200 bg-red-50 text-red-700'>
              <AlertCircle className='size-4' />
              <AlertDescription className='text-xs'>
                {previewErrorText}
              </AlertDescription>
            </Alert>
          )}
          {overpaymentAmount > 0 && (
            <Alert className='border-amber-200 bg-amber-50 text-amber-800'>
              <Info className='size-4' />
              <AlertDescription className='text-xs'>
                {symbol}
                {overpaymentAmount.toFixed(2)} exceeds the selected charge
                balances and will be added to the parent wallet.
              </AlertDescription>
            </Alert>
          )}
          {confirmedPreview?.reallocatedByStudioPolicy && (
            <p className='text-xs text-blue-700'>
              The amount will cover the oldest charges first, per your studio
              settings.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              setOpen(false);
            }}
            disabled={takePaymentMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {takePaymentMutation.isPending && (
              <Loader2 className='mr-1.5 size-4 animate-spin' />
            )}
            {method === 'card'
              ? `Charge ${symbol}${total.toFixed(2)}`
              : `Record ${symbol}${total.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function ChargeRow({
  invoice,
  symbol,
  checked,
  disabled,
  allowPartial,
  draftAmount,
  onToggle,
  onAmountChange,
}: {
  invoice: PayableInvoice;
  symbol: string;
  checked: boolean;
  disabled: boolean;
  allowPartial: boolean;
  draftAmount: string | undefined;
  onToggle: (checked: boolean) => void;
  onAmountChange: (value: string) => void;
}) {
  return (
    <div
      className={`rounded-md border p-3 ${checked ? 'border-primary/40 bg-muted/30' : ''} ${
        invoice.isPayable ? '' : 'opacity-60'
      }`}
    >
      <div className='flex items-start gap-3'>
        <Checkbox
          id={`take-payment-invoice-${invoice.invoiceId}`}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(value) => {
            onToggle(value === true);
          }}
          className='mt-0.5'
        />
        <div className='min-w-0 flex-1'>
          <Label
            htmlFor={`take-payment-invoice-${invoice.invoiceId}`}
            className='cursor-pointer font-medium'
          >
            {invoice.description}
          </Label>
          <p className='mt-0.5 text-xs text-muted-foreground'>
            {invoice.studentName} · Due {formatDueDate(invoice.dueDate)} ·{' '}
            <span className='capitalize'>{invoice.status}</span>
          </p>
          {invoice.blockedReason && (
            <p className='mt-1 text-xs text-amber-700'>
              {invoice.blockedReason}
            </p>
          )}
        </div>
        <div className='shrink-0 text-right'>
          <p className='font-semibold tabular-nums'>
            {symbol}
            {invoice.remainingBalance.toFixed(2)}
          </p>
          {invoice.remainingBalance < invoice.originalAmount && (
            <p className='text-[11px] text-muted-foreground'>
              of {symbol}
              {invoice.originalAmount.toFixed(2)}
            </p>
          )}
        </div>
      </div>

      {allowPartial && checked && invoice.isPayable && (
        <div className='mt-3 flex items-center gap-2 border-t pt-3'>
          <Label
            htmlFor={`take-payment-amount-${invoice.invoiceId}`}
            className='text-xs text-muted-foreground'
          >
            Amount to apply
          </Label>
          <div className='relative'>
            <span className='pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground'>
              {symbol}
            </span>
            <Input
              id={`take-payment-amount-${invoice.invoiceId}`}
              type='number'
              inputMode='decimal'
              min={0.01}
              step={0.01}
              max={invoice.remainingBalance}
              value={draftAmount ?? invoice.remainingBalance.toFixed(2)}
              onChange={(event) => {
                onAmountChange(event.target.value);
              }}
              className='h-8 w-28 pl-6 tabular-nums'
            />
          </div>
        </div>
      )}
    </div>
  );
}
